import { AdminShell } from "@/components/admin/admin-shell";
import { Shell } from "@/components/shell";
import { ThemeProvider } from "@/components/theme-provider";
import { ActivityPage } from "@/pages/activity";
import { AdminCandidateEditPage } from "@/pages/admin/candidate-edit";
import { AdminCandidatesPage } from "@/pages/admin/candidates";
import { AdminSearchDetailPage } from "@/pages/admin/search-detail";
import { AdminSearchesPage } from "@/pages/admin/searches";
import { AdminUsersPage } from "@/pages/admin/users";
import { AttentionPage } from "@/pages/attention";
import { CSuitePage } from "@/pages/csuite";
import { GeneralPage } from "@/pages/general";
import { AuthCallbackPage, type CallbackState, LoginPage } from "@/pages/login";
import { NewRolePage } from "@/pages/new-role";
import { OverviewPage } from "@/pages/overview";
import { SearchDetailPage } from "@/pages/search-detail";
import { TeamAccessPage } from "@/pages/team-access";
import { VpDirectorPage } from "@/pages/vp-director";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, RouterProvider, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./app.css";

const rootRoute = createRootRoute({ component: () => <Outlet /> });

// Auth screens — standalone (no app shell; you're not signed in yet). ONE design, two entry points: the
// client portal and OW staff. Magic-link only, so there's no password anywhere. `/auth/verify` is where the
// emailed link lands; `?state=` selects which outcome to preview (verifying / success / expired / invalid).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => <LoginPage variant="client" />,
});
const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin-login",
  component: () => <LoginPage variant="staff" />,
});
const CALLBACK_STATES: CallbackState[] = ["verifying", "success", "expired", "invalid"];
const authVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/verify",
  validateSearch: (search: Record<string, unknown>): { state: CallbackState } => ({
    state: CALLBACK_STATES.includes(search.state as CallbackState) ? (search.state as CallbackState) : "verifying",
  }),
  component: AuthVerifyRoute,
});
function AuthVerifyRoute() {
  const { state } = authVerifyRoute.useSearch();
  return <AuthCallbackPage state={state} />;
}

// `?candidate=<id>` opens that candidate in the role page's right-side drawer — so the drawer is
// URL-addressable (deep-linkable, back-button closes it, prev/next is just a param update).
// `?from=<origin>` records how you arrived (Needs attention, a tier, …) so the top bar can offer a
// context-aware "back" to that origin instead of only ever returning to the overview. Absent/unknown ⇒
// the overview is the sensible default. Validated against the known set so an origin survives the round-trip
// (the router strips search params the validator doesn't return).
const ROLE_ORIGINS = ["attention", "overview", "csuite", "vp-director", "general"] as const;
type RoleOrigin = (typeof ROLE_ORIGINS)[number];
const validateSearchDetailSearch = (search: Record<string, unknown>): { candidate?: string; from?: RoleOrigin } => ({
  candidate: typeof search.candidate === "string" ? search.candidate : undefined,
  from: ROLE_ORIGINS.includes(search.from as RoleOrigin) ? (search.from as RoleOrigin) : undefined,
});

/** The client surfaces are mounted twice — once for each role (audit C2). The viewer tree lives at the
 *  root paths; the admin tree lives under `/admin-view` and renders the same pages with admin
 *  affordances (configure pipeline, edit stage/status). The URL is what decides the role, so both
 *  experiences are previewable without an auth system. `prefix` keeps the two trees from colliding. */
function makeClientRoutes(parent: typeof clientLayout | typeof adminViewLayout, prefix: "" | "/admin-view") {
  return [
    createRoute({ getParentRoute: () => parent, path: prefix || "/", component: OverviewPage }),
    createRoute({ getParentRoute: () => parent, path: `${prefix}/csuite`, component: CSuitePage }),
    createRoute({ getParentRoute: () => parent, path: `${prefix}/vp-director`, component: VpDirectorPage }),
    createRoute({ getParentRoute: () => parent, path: `${prefix}/general`, component: GeneralPage }),
    createRoute({ getParentRoute: () => parent, path: `${prefix}/activity`, component: ActivityPage }),
    createRoute({
      getParentRoute: () => parent,
      path: `${prefix}/search/$searchId`,
      component: SearchDetailPage,
      validateSearch: validateSearchDetailSearch,
    }),
  ];
}

// --- Client area (read-only dashboard, viewer role) ---
const clientLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "client",
  // biome-ignore lint/a11y/useValidAriaRole: `role` is a Shell component prop, not an ARIA attribute
  component: () => <Shell role="viewer" />,
});

// --- Client area previewed as OW staff (admin role) ---
const adminViewLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin-view",
  // biome-ignore lint/a11y/useValidAriaRole: `role` is a Shell component prop, not an ARIA attribute
  component: () => <Shell role="admin" />,
});

// --- Admin area (internal back-office) ---
const adminLayout = createRoute({ getParentRoute: () => rootRoute, path: "/admin", component: AdminShell });
const adminIndex = createRoute({
  getParentRoute: () => adminLayout,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/searches" });
  },
});
const adminSearches = createRoute({
  getParentRoute: () => adminLayout,
  path: "searches",
  component: AdminSearchesPage,
});
const adminSearchDetail = createRoute({
  getParentRoute: () => adminLayout,
  path: "searches/$searchId",
  component: AdminSearchDetailPage,
});
const adminCandidates = createRoute({
  getParentRoute: () => adminLayout,
  path: "candidates",
  component: AdminCandidatesPage,
});
const adminCandidateEdit = createRoute({
  getParentRoute: () => adminLayout,
  path: "candidates/$candidateId",
  component: AdminCandidateEditPage,
});
const adminUsers = createRoute({ getParentRoute: () => adminLayout, path: "users", component: AdminUsersPage });

// Admin-only destinations under the client's admin tree — the Needs attention queue and Team & access.
// These exist ONLY under `/admin-view`, so the read-only client never has a route to them. (Activity is NOT
// here — it's a shared route via makeClientRoutes, since the client also gets a filtered, read-only trail.)
const adminViewAttention = createRoute({
  getParentRoute: () => adminViewLayout,
  path: "/admin-view/attention",
  component: AttentionPage,
});
const adminViewTeam = createRoute({
  getParentRoute: () => adminViewLayout,
  path: "/admin-view/team",
  component: TeamAccessPage,
});
const adminViewNewRole = createRoute({
  getParentRoute: () => adminViewLayout,
  path: "/admin-view/roles/new",
  component: NewRolePage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  adminLoginRoute,
  authVerifyRoute,
  clientLayout.addChildren(makeClientRoutes(clientLayout, "")),
  adminViewLayout.addChildren([
    ...makeClientRoutes(adminViewLayout, "/admin-view"),
    adminViewAttention,
    adminViewTeam,
    adminViewNewRole,
  ]),
  adminLayout.addChildren([
    adminIndex,
    adminSearches,
    adminSearchDetail,
    adminCandidates,
    adminCandidateEdit,
    adminUsers,
  ]),
]);

const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1 } } });

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
